/**
 * Where a thumbnail lands when it is put on the board (pure): centred in the
 * current viewport, capped to a readable width, aspect preserved. The nth
 * placement in a row is nudged down-right so two picks placed one after the
 * other do not stack pixel-perfectly and read as one.
 */
import { GRID_ORIGIN, screenToBoard, type Camera } from "@kola/shared";

/** Displayed width in board units — wide enough to read the lettering. */
export const THUMB_ELEMENT_MAX_W = 480;
/** Nudge between successive placements, in board units. */
export const THUMB_PLACE_STEP = 24;

export interface ViewportSize {
  width: number;
  height: number;
}

export interface BoardRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Fit a frame into `THUMB_ELEMENT_MAX_W`, preserving its aspect. */
export function fitThumb(naturalW: number, naturalH: number): { w: number; h: number } {
  if (naturalW <= 0 || naturalH <= 0) {
    return { w: THUMB_ELEMENT_MAX_W, h: Math.round((THUMB_ELEMENT_MAX_W * 9) / 16) };
  }
  const scale = Math.min(1, THUMB_ELEMENT_MAX_W / naturalW);
  return { w: Math.round(naturalW * scale), h: Math.round(naturalH * scale) };
}

export function thumbPlacement(
  natural: { w: number; h: number },
  camera: Camera,
  viewport: ViewportSize,
  nudge = 0,
): BoardRect {
  const size = fitThumb(natural.w, natural.h);
  const center = screenToBoard(camera, { x: viewport.width / 2, y: viewport.height / 2 });
  const offset = nudge * THUMB_PLACE_STEP;
  return {
    x: Math.round(center.x - size.w / 2 + offset),
    y: Math.round(center.y - size.h / 2 + offset),
    w: size.w,
    h: size.h,
  };
}

/** The slice of the canvas store a placement reads. */
export interface CanvasLike {
  getState(): { camera: Camera; viewport: ViewportSize };
  subscribe(listener: (state: { camera: Camera; viewport: ViewportSize }) => void): () => void;
}

export function hasViewport(viewport: ViewportSize): boolean {
  return viewport.width > 0 && viewport.height > 0;
}

/**
 * The camera and viewport to place against, once the canvas HAS one.
 *
 * In Script view the canvas is not mounted and its viewport is 0×0 (or stale
 * from an earlier visit). The panel switches the board to Grid before placing,
 * and this waits for the ResizeObserver to publish the real size — resolving
 * at once when a size is already known, and giving up after `timeoutMs` with
 * whatever the store holds, so a placement can never hang.
 */
export function waitForCanvas(
  canvas: CanvasLike,
  timeoutMs = 1500,
): Promise<{ camera: Camera; viewport: ViewportSize }> {
  const now = canvas.getState();
  if (hasViewport(now.viewport)) return Promise.resolve(now);
  return new Promise((resolve) => {
    let done = false;
    const finish = (state: { camera: Camera; viewport: ViewportSize }) => {
      if (done) return;
      done = true;
      unsubscribe();
      clearTimeout(timer);
      resolve(state);
    };
    const unsubscribe = canvas.subscribe((state) => {
      if (hasViewport(state.viewport)) finish(state);
    });
    const timer = setTimeout(() => finish(canvas.getState()), timeoutMs);
  });
}

/**
 * Where a thumbnail goes when there is no viewport to centre on at all: just
 * above the table's top-left corner, so the default framing (which puts that
 * corner in view) shows it.
 */
export function fallbackPlacement(natural: { w: number; h: number }, nudge = 0): BoardRect {
  const size = fitThumb(natural.w, natural.h);
  const offset = nudge * THUMB_PLACE_STEP;
  return {
    x: GRID_ORIGIN.x + offset,
    y: GRID_ORIGIN.y - size.h - 24 + offset,
    w: size.w,
    h: size.h,
  };
}
